import { Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Subject, BehaviorSubject, debounceTime, distinctUntilChanged, switchMap, catchError, of } from 'rxjs';

export interface AutoSaveOptions {
  debounceMs?: number;
  validateField?: boolean;
  onSuccess?: (value: any) => void;
  onError?: (error: any) => void;
}

export interface AutoSaveState {
  isSaving: boolean;
  lastSaved: Date | null;
  error: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class AutoSaveService {
  private saveStates = new Map<string, BehaviorSubject<AutoSaveState>>();
  private saveTriggers = new Map<string, Subject<any>>();

  /**
   * Initialize auto-save for a specific field
   */
  initializeAutoSave(
    fieldId: string, 
    saveFunction: (value: any) => Promise<any> | any,
    options: AutoSaveOptions = {}
  ): void {
    const {
      debounceMs = 300,
      validateField = true,
      onSuccess,
      onError
    } = options;

    // Initialize state if not exists
    if (!this.saveStates.has(fieldId)) {
      this.saveStates.set(fieldId, new BehaviorSubject<AutoSaveState>({
        isSaving: false,
        lastSaved: null,
        error: null
      }));
    }

    // Initialize trigger if not exists
    if (!this.saveTriggers.has(fieldId)) {
      this.saveTriggers.set(fieldId, new Subject<any>());
    }

    const trigger$ = this.saveTriggers.get(fieldId)!;
    const state$ = this.saveStates.get(fieldId)!;

    // Set up debounced auto-save
    trigger$.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      switchMap(value => {
        // Update state to saving
        state$.next({
          isSaving: true,
          lastSaved: state$.value.lastSaved,
          error: null
        });

        // Execute save function
        const savePromise = Promise.resolve(saveFunction(value));
        return savePromise.then(
          result => ({ success: true, result, value }),
          error => ({ success: false, error, value })
        );
      }),
      catchError(error => of({ success: false, error, value: null }))
    ).subscribe(result => {
        if (result.success) {
          state$.next({
            isSaving: false,
            lastSaved: new Date(),
            error: null
          });
          onSuccess?.(result.value);
        } else {
          state$.next({
            isSaving: false,
            lastSaved: state$.value.lastSaved,
            error: (result as any).error?.message || 'Save failed'
          });
          onError?.((result as any).error);
        }
    });
  }

  /**
   * Trigger auto-save for a field
   */
  triggerSave(fieldId: string, value: any): void {
    const trigger$ = this.saveTriggers.get(fieldId);
    if (trigger$) {
      trigger$.next(value);
    }
  }

  /**
   * Get auto-save state for a field
   */
  getSaveState(fieldId: string): BehaviorSubject<AutoSaveState> | null {
    return this.saveStates.get(fieldId) || null;
  }

  /**
   * Check if field is currently saving
   */
  isSaving(fieldId: string): boolean {
    const state$ = this.saveStates.get(fieldId);
    return state$?.value.isSaving || false;
  }

  /**
   * Get last saved timestamp
   */
  getLastSaved(fieldId: string): Date | null {
    const state$ = this.saveStates.get(fieldId);
    return state$?.value.lastSaved || null;
  }

  /**
   * Get save error
   */
  getError(fieldId: string): string | null {
    const state$ = this.saveStates.get(fieldId);
    return state$?.value.error || null;
  }

  /**
   * Validate form control before saving
   */
  validateField(formControl: any): boolean {
    if (!formControl) return false;
    
    // Check if field is valid and has been touched/modified
    return formControl.valid && (formControl.dirty || formControl.touched);
  }

  /**
   * Clean up auto-save for a field
   */
  cleanup(fieldId: string): void {
    const trigger$ = this.saveTriggers.get(fieldId);
    const state$ = this.saveStates.get(fieldId);
    
    if (trigger$) {
      trigger$.complete();
      this.saveTriggers.delete(fieldId);
    }
    
    if (state$) {
      state$.complete();
      this.saveStates.delete(fieldId);
    }
  }

  /**
   * Clean up all auto-save instances
   */
  cleanupAll(): void {
    this.saveTriggers.forEach((trigger$, fieldId) => {
      trigger$.complete();
    });
    this.saveStates.forEach((state$, fieldId) => {
      state$.complete();
    });
    
    this.saveTriggers.clear();
    this.saveStates.clear();
  }
}
